import React, { useState, useEffect } from 'react';
import { Newspaper, ExternalLink } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NewsTicker = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    fetchNews();
    // Refresh news every 5 minutes
    const interval = setInterval(fetchNews, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchNews = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/news`, {
        params: { category: 'todos', limit: 8 }
      });
      
      const newsData = response.data.articles || response.data;
      
      if (newsData && newsData.length > 0) {
        setNews(newsData.slice(0, 8));
        setError(null);
      }
    } catch (err) {
      console.error('Error fetching news:', err);
      setError('No se pudieron cargar las noticias');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-r from-green-700 to-green-600 py-2">
        <div className="max-w-[1400px] mx-auto px-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-white/20 px-3 py-1 rounded-full">
              <Newspaper className="w-4 h-4 text-white" />
              <span className="text-white text-sm font-semibold">EN VIVO</span>
            </div>
            <span className="text-white/80 text-sm">Cargando noticias...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || news.length === 0) {
    return null;
  }

  // Create ticker items - duplicate for seamless loop
  const tickerItems = [...news, ...news, ...news];

  return (
    <div 
      data-testid="news-ticker"
      className="bg-gradient-to-r from-green-700 via-green-600 to-green-700 py-2 shadow-md overflow-hidden"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="flex items-center gap-4 px-4">
        {/* Live Badge */}
        <div className="flex-shrink-0 flex items-center gap-2 bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-full border border-white/30">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
          </span>
          <Newspaper className="w-4 h-4 text-white" />
          <span className="text-white text-xs font-bold uppercase tracking-wide">Noticias</span>
        </div>

        {/* Scrolling News Container */}
        <div className="flex-1 overflow-hidden relative">
          {/* Ticker Track */}
          <div 
            className="ticker-track flex gap-12"
            style={{
              animationPlayState: isPaused ? 'paused' : 'running'
            }}
          >
            {tickerItems.map((item, index) => (
              <a
                key={`${item.id}-${index}`}
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-white hover:text-yellow-200 transition-colors group flex-shrink-0"
              >
                <span className="text-yellow-400 font-bold text-lg">•</span>
                <span className="text-sm font-medium whitespace-nowrap">{item.title}</span>
                <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
              </a>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        .ticker-track {
          animation: tickerScroll 24s linear infinite;
          width: max-content;
        }
        
        @keyframes tickerScroll {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-33.333%);
          }
        }
      `}</style>
    </div>
  );
};

export default NewsTicker;
